import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import {
  extractPlainParagraphsFromFaqHtml,
  looksLikeFaqHtml,
  sanitizeFaqAnswerHtml,
} from "@/utils/faqHubAnswerHtml";
import FaqHubHighlightText from "./FaqHubHighlightText";

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// Split an answer string into rendering blocks: paragraphs and bullet lists.
// Lines that begin with "-", "•" or "*" become list items; consecutive bullet
// lines are merged into a single <ul>. Blank lines are treated as separators.
function parseAnswerBlocks(answer) {
  const lines = String(answer || "").split(/\r?\n/);
  const blocks = [];
  let currentList = null;
  for (const raw of lines) {
    const line = raw.trim();
    const bullet = line.match(/^[-•*]\s+(.*)$/);
    if (bullet) {
      if (!currentList) {
        currentList = { type: "ul", items: [] };
        blocks.push(currentList);
      }
      currentList.items.push(bullet[1]);
      continue;
    }
    currentList = null;
    if (line === "") continue;
    blocks.push({ type: "p", text: line });
  }
  return blocks;
}

// Minimal inline renderer: splits text on **bold**/__bold__ markers, then
// runs each resulting chunk through the search-highlighter.
// Also auto-bolds a leading "Name:" in bullet items when no explicit ** is present.
function InlineMd({ text, query, autoboldColon = false }) {
  let source = text == null ? "" : String(text);

  // Fallback: if the text has no explicit bold and caller opted in (bullets),
  // wrap the pre-colon head in ** so it bolds like the rest of the design.
  if (autoboldColon && !/\*\*|__/.test(source)) {
    const idx = source.indexOf(":");
    if (idx > 0 && idx < 80) {
      source = `**${source.slice(0, idx)}**${source.slice(idx)}`;
    }
  }

  const parts = source.split(/(\*\*[^*]+\*\*|__[^_]+__)/g).filter(Boolean);
  return (
    <>
      {parts.map((part, i) => {
        const boldMatch = part.match(/^(?:\*\*|__)([\s\S]+)(?:\*\*|__)$/);
        if (boldMatch) {
          return (
            <strong key={i} className="font-semibold text-[#161616]">
              <FaqHubHighlightText text={boldMatch[1]} query={query} />
            </strong>
          );
        }
        return (
          <FaqHubHighlightText key={i} text={part} query={query} />
        );
      })}
    </>
  );
}

function AnswerBody({ answer, query }) {
  const htmlMode = useMemo(() => looksLikeFaqHtml(answer), [answer]);
  const sanitizedHtml = useMemo(
    () => (htmlMode ? sanitizeFaqAnswerHtml(answer) : ""),
    [htmlMode, answer]
  );
  const htmlPlainParagraphs = useMemo(
    () => (htmlMode ? extractPlainParagraphsFromFaqHtml(answer) : []),
    [htmlMode, answer]
  );
  const blocks = useMemo(() => {
    if (looksLikeFaqHtml(answer)) return [];
    return parseAnswerBlocks(answer);
  }, [answer]);

  const q = String(query || "").trim();

  if (htmlMode) {
    if (q.length >= 2) {
      return (
        <div className="space-y-3">
          {htmlPlainParagraphs.map((para, i) => (
            <p key={i} className="m-0 leading-relaxed">
              <FaqHubHighlightText text={para} query={query} />
            </p>
          ))}
        </div>
      );
    }
    return (
      <div
        className="faq-hub-answer-html text-[#4d4d4d] [&_a]:text-red [&_a]:underline [&_b]:font-semibold [&_b]:text-[#161616] [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_strong]:text-[#161616] [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  }

  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === "ul") {
          return (
            <ul
              key={i}
              className="list-disc pl-5 mb-3 last:mb-0 space-y-1.5 marker:text-[#ef4136]"
            >
              {block.items.map((item, j) => (
                <li key={j} className="leading-relaxed">
                  <InlineMd text={item} query={query} autoboldColon />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="mb-3 last:mb-0 leading-relaxed">
            <InlineMd text={block.text} query={query} />
          </p>
        );
      })}
    </>
  );
}

function Chevron({ open }) {
  return (
    <Image
      src="/assets/icons/chevron-down.svg"
      alt=""
      width={18}
      height={18}
      aria-hidden
      className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    />
  );
}

export default function FaqHubFaqPanel({ faqs, sectionTitle = "Product FAQs" }) {
  const [hashFaqId] = useState(() => {
    if (typeof window === "undefined") return null;
    const match = window.location.hash.match(/^#faq-(\d+)$/);
    return match ? Number(match[1]) : null;
  });

  const validInitial =
    hashFaqId != null && faqs?.some((f) => f.id === hashFaqId)
      ? hashFaqId
      : null;

  // First FAQ open by default when nothing else pins it.
  const [openIds, setOpenIds] = useState(() => new Set());
  const [hasSeededDefault, setHasSeededDefault] = useState(false);

  // Seed the default-open FAQ (first item) once, only when no hash target exists.
  useEffect(() => {
    if (hasSeededDefault) return;
    if (!faqs?.length) return;
    if (validInitial != null) return; // hash target will open its own FAQ
    setOpenIds(new Set([faqs[0].id]));
    setHasSeededDefault(true);
  }, [faqs, validInitial, hasSeededDefault]);

  // Whenever the hash target is resolved, open it (overrides the default seed).
  useEffect(() => {
    if (validInitial == null) return;
    setOpenIds((prev) => {
      const n = new Set(prev);
      n.add(validInitial);
      return n;
    });
    setHasSeededDefault(true);
  }, [validInitial]);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  const matchingIds = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q.length < 2) return new Set();
    const next = new Set();
    (faqs || []).forEach((f) => {
      const qn = (f.question || "").toLowerCase();
      const an = String(f.answer || "").toLowerCase();
      if (qn.includes(q) || an.includes(q)) next.add(f.id);
    });
    return next;
  }, [faqs, debouncedSearch]);

  useEffect(() => {
    if (matchingIds.size === 0) return;
    setOpenIds((prev) => {
      const n = new Set(prev);
      matchingIds.forEach((id) => n.add(id));
      return n;
    });
  }, [matchingIds]);

  useEffect(() => {
    if (!validInitial) return;
    const t = setTimeout(() => {
      document
        .getElementById(`faq-${validInitial}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    return () => clearTimeout(t);
  }, [validInitial]);

  useEffect(() => {
    if (debouncedSearch.trim().length < 2) return;
    const first = (faqs || []).find((f) => matchingIds.has(f.id));
    if (!first) return;
    const t = setTimeout(() => {
      document
        .getElementById(`faq-${first.id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 120);
    return () => clearTimeout(t);
  }, [debouncedSearch, faqs, matchingIds]);

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  if (!faqs?.length) {
    return (
      <p className="text-[#666] mt-8">No published FAQs for this product yet.</p>
    );
  }

  return (
    <div id="product-faqs">
      <h2 className="font-mukta text-[1.5rem] md:text-[1.75rem] font-semibold text-[#161616]">
        {sectionTitle}
      </h2>
      <div className="mt-4 w-full max-w-md">
        <label htmlFor="faq-inpage-search" className="sr-only">
          Search on this page
        </label>
        <div className="flex h-[44px] items-center gap-2 rounded-[8px] border border-[#e0e0e0] bg-white px-3">
          <Image
            src="/assets/icons/search.svg"
            alt=""
            width={18}
            height={18}
            aria-hidden
          />
          <input
            id="faq-inpage-search"
            type="search"
            placeholder="Search by FAQ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-0 flex-1 border-none bg-transparent font-mukta text-[0.88rem] text-[#333] outline-none placeholder:text-[#a8a8a8]"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {faqs.map((faq) => {
          const open = openIds.has(faq.id);
          return (
            <article
              key={faq.id}
              id={`faq-${faq.id}`}
              className={`scroll-mt-24 rounded-[10px] transition-colors ${
                open ? "bg-[#f4f4f4]" : "bg-transparent"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleOpen(faq.id)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-4 px-4 py-4 md:py-5 text-left"
              >
                <h3 className="flex-1 font-mukta text-[0.95rem] md:text-[1rem] font-semibold text-[#161616] leading-snug m-0">
                  <FaqHubHighlightText
                    text={faq.question}
                    query={debouncedSearch}
                  />
                </h3>
                <Chevron open={open} />
              </button>
              {open ? (
                <div className="px-4 pb-5 font-mukta text-[0.9rem] text-[#4d4d4d]">
                  <AnswerBody
                    answer={faq.answer}
                    query={debouncedSearch}
                  />
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}
